import { useCallback, useEffect, useState } from 'react';
import { api } from '../api';
import type { SourceCleanupItem, SourceCleanupOverview } from '../types';
import { formatBytes, formatDate } from '../utils';
import { Icon } from './Icon';

function itemStatus(item: SourceCleanupItem): { label: string; tone: 'processing' | 'failed' | 'neutral' } {
  if (item.activeTranscription) return { label: 'Subtitle job active', tone: 'processing' };
  if (item.error) return { label: 'Cleanup failed', tone: 'failed' };
  return { label: 'Pending retry', tone: 'neutral' };
}

export function SourceCleanupPanel({ notify }: {
  notify: (message: string, tone?: 'success' | 'error') => void;
}) {
  const [overview, setOverview] = useState<SourceCleanupOverview | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setOverview(await api.sourceCleanup());
    } catch (error) {
      notify(error instanceof Error ? error.message : 'Could not load source cleanup status.', 'error');
    } finally {
      setLoading(false);
    }
  }, [notify]);

  useEffect(() => { void load(); }, [load]);

  const retryOne = async (item: SourceCleanupItem) => {
    setBusy(item.videoId);
    try {
      const next = await api.retrySourceCleanup(item.videoId);
      setOverview(next);
      if (next.items.some((candidate) => candidate.videoId === item.videoId)) {
        notify('Source cleanup is still deferred. Review its latest status.', 'error');
      } else {
        notify(`Removed the retained source for “${item.videoTitle}”.`);
      }
    } catch (error) {
      notify(error instanceof Error ? error.message : 'Source cleanup retry failed.', 'error');
      await load();
    } finally {
      setBusy('');
    }
  };

  const retryAll = async () => {
    setBusy('all');
    try {
      const next = await api.retryAllSourceCleanup();
      setOverview(next);
      if (next.summary.failed) notify(`${next.summary.failed} source cleanup operation(s) still need attention.`, 'error');
      else if (next.summary.total) notify(`${next.summary.total} source file(s) remain deferred and will retry automatically.`);
      else notify('All eligible retained sources were removed.');
    } catch (error) {
      notify(error instanceof Error ? error.message : 'Source cleanup retry failed.', 'error');
      await load();
    } finally {
      setBusy('');
    }
  };

  if (loading && !overview) {
    return <article className="media-storage-card source-cleanup-panel"><div className="panel-loading"><span className="spinner spinner--large" />Loading source cleanup status…</div></article>;
  }
  if (!overview) {
    return <article className="media-storage-card source-cleanup-panel"><div className="empty-state"><Icon name="warning" size={26} /><strong>Cleanup status unavailable</strong><button className="button button--secondary" type="button" onClick={() => void load()}>Try again</button></div></article>;
  }

  const tone = !overview.enabled ? 'neutral' : overview.summary.failed ? 'failed' : overview.summary.total ? 'processing' : 'ready';
  return <article className="media-storage-card source-cleanup-panel">
    <div className="media-storage-card__heading source-cleanup-panel__heading">
      <div><span className="eyebrow">Source lifecycle</span><h2>Retained source cleanup</h2><p>Original MP4 files are removed only after their verified R2 publication is active. Failed removals remain tracked and retry automatically every {overview.retryIntervalMinutes} minutes.</p></div>
      <span className={`status-badge status-badge--${tone}`}><span className="status-badge__dot" />{!overview.enabled ? 'Disabled' : overview.summary.failed ? 'Needs attention' : overview.summary.total ? 'Cleanup pending' : 'All clear'}</span>
    </div>

    {!overview.enabled && <div className="system-alert"><Icon name="warning" size={19} /><div><strong>Automatic source deletion is disabled</strong><span>The current retention policy keeps original uploads. Retry actions remain unavailable.</span></div></div>}

    <div className="source-cleanup-summary">
      <div><span>Retained sources</span><strong>{overview.summary.total.toLocaleString()}</strong></div>
      <div><span>VPS space pending release</span><strong>{formatBytes(overview.summary.bytes)}</strong></div>
      <div><span>Failed cleanup</span><strong>{overview.summary.failed.toLocaleString()}</strong></div>
      <div><span>Waiting for subtitles</span><strong>{overview.summary.waiting.toLocaleString()}</strong></div>
    </div>

    <div className="source-cleanup-actions">
      <button className="button button--primary" type="button" disabled={!overview.enabled || !overview.summary.total || Boolean(busy)} onClick={() => void retryAll()}>{busy === 'all' ? 'Retrying…' : 'Retry all'}</button>
      <button className="button button--secondary" type="button" disabled={Boolean(busy) || loading} onClick={() => void load()}>{loading ? 'Refreshing…' : 'Refresh'}</button>
    </div>

    {!overview.items.length ? <div className="empty-state source-cleanup-empty"><span className="empty-state__icon"><Icon name="check" size={24} /></span><strong>No retained Ready sources</strong><span>Verified R2 videos have released their original MP4 files from VPS storage.</span></div> : <div className="video-table-wrap source-cleanup-table-wrap"><table className="video-table source-cleanup-table"><thead><tr><th>Video</th><th>Member</th><th>Source</th><th>Status</th><th>Last attempt</th><th>Action</th></tr></thead><tbody>{overview.items.map((item) => {
      const status = itemStatus(item);
      return <tr key={item.videoId}>
        <td><div className="source-cleanup-video"><strong>{item.videoTitle}</strong><span>{item.originalFilename || item.videoId}</span></div></td>
        <td><div className="source-cleanup-video"><strong>{item.memberName}</strong><span>{item.memberEmail}</span></div></td>
        <td>{formatBytes(item.sourceBytes)}</td>
        <td><span className={`status-badge status-badge--${status.tone}`}><span className="status-badge__dot" />{status.label}</span>{item.error && <small className="source-cleanup-error" title={item.error}>{item.error}</small>}{item.failures > 0 && <small>{item.failures} failed attempt{item.failures === 1 ? '' : 's'}</small>}</td>
        <td>{formatDate(item.attemptedAt ?? undefined)}</td>
        <td><button className="button button--secondary" type="button" disabled={!overview.enabled || item.activeTranscription || Boolean(busy)} onClick={() => void retryOne(item)}>{busy === item.videoId ? 'Retrying…' : item.activeTranscription ? 'Waiting' : 'Retry cleanup'}</button></td>
      </tr>;
    })}</tbody></table></div>}
  </article>;
}
